import type {CouponHistory} from './types';

import {Button, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import historyEmptyState from 'assets/images/promotion-center/new/historyEmptyState.svg';
import Empty from 'common/empty/Empty';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getCouponHistoryList} from 'store/slices/coupon/thunk';

import {COUPON_ROUTES} from '../constants';
import {getCouponVendorId} from '../utils';
import CouponHistoryList from './components/CouponHistoryList/CouponHistoryList';

const COUPON_PAGE_SIZE = 15;

const CouponList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const vendorId = getCouponVendorId();
  const {historyList, loading} = useAppSelector((state) => state.coupon);
  const history = historyList as CouponHistory | null;
  const isLoadingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadCoupons = useCallback(
    (page: number) =>
      dispatch(
        getCouponHistoryList({
          vendor_id: vendorId,
          page_size: COUPON_PAGE_SIZE,
          page
        })
      ),
    [dispatch, vendorId]
  );

  useEffect(() => {
    void loadCoupons(1);
  }, [loadCoupons]);

  const loadMore = () => {
    if (isLoadingMoreRef.current || !history || history.page >= history.total_pages) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    void loadCoupons(history.page + 1)
      .unwrap()
      .catch(() => undefined)
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  };

  const coupons = history?.coupons ?? [];
  const isInitialLoading = loading && coupons.length === 0;

  return (
    <PageLayout
      footerPosition="static"
      contentId="coupon-history-list"
      footer={
        <Button fullWidth onClick={() => navigate(COUPON_ROUTES.add)}>
          فعال سازی کوپن جدید
        </Button>
      }
    >
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : history && coupons.length > 0 ? (
        <CouponHistoryList history={history} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
      ) : (
        <Empty illustration={historyEmptyState} className="min-h-60 flex-1 gap-1 px-4 text-center">
          <p className="text-subtitle-md text-neutral-fg">بسته‌ای برای نمایش وجود ندارد</p>
          <p className="text-body-sm text-gray-34">تا به حال کوپنی را فعال نکرده‌اید!</p>
        </Empty>
      )}
    </PageLayout>
  );
};

export default CouponList;
